/**
 * ChartContainer Component
 *
 * Reusable wrapper for charts with title, tooltip, and loading state
 */

import React from 'react';
import { Paper, Typography, Box, Skeleton, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export interface ChartContainerProps {
  title: string;
  subtitle?: string;
  tooltip?: string;
  children: React.ReactNode;
  loading?: boolean;
  height?: number | string;
  headerAction?: React.ReactNode;
}

const ChartContainer: React.FC<ChartContainerProps> = ({
  title,
  subtitle,
  tooltip,
  children,
  loading = false,
  height = 350,
  headerAction,
}) => {
  return (
    <Paper sx={{ p: 3, height: '100%' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {title}
            </Typography>
            {tooltip && (
              <Tooltip title={tooltip} arrow placement="top">
                <InfoOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            )}
          </Box>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        {headerAction && <Box>{headerAction}</Box>}
      </Box>

      {/* Chart Content */}
      <Box sx={{ height: typeof height === 'number' ? `${height}px` : height }}>
        {loading ? (
          <Skeleton variant="rectangular" height="100%" animation="wave" />
        ) : (
          children
        )}
      </Box>
    </Paper>
  );
};

export default ChartContainer;
